import { ref } from 'vue';
import type {
  AlgoliaSearch,
  AlgoliaSearchPayloadRequestOptions,
  AlgoliaSearchProduct, AlgoliaSearchProductsItem,
  ProductRaw
} from '#constants/types/algolia';
import Product from '#models/product';

import { useUiStore } from '~/store/ui';
import { useUserStore } from '~/store/user';
import { useGlobalContentStore } from '~/store/globalContent';
import { NorceUserTypes } from '#constants/norceCodes';
const lastError = ref(null) as any;
const lastErrorCode = ref<number | null>(null);
const lastResponse = ref(null) as any;
const pending = ref(false);
const CACHE_TTL_MS = 5 * 60 * 1000;
const responseCache = new Map<string, { ts: number; data: any }>();
const pendingCache = new Map<string, Promise<any>>();

const stableStringify = (value: unknown): string => {
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(',')}]`;
  }
  if (value && typeof value === 'object') {
    const keys = Object.keys(value as Record<string, unknown>).sort();
    return `{${keys.map((key) => `${JSON.stringify(key)}:${stableStringify((value as Record<string, unknown>)[key])}`).join(',')}}`;
  }
  return JSON.stringify(value);
};

export default function useAlgoliaFetch() {
  /**
   * Gets a single product
   * After getting the result, use Product.create(result.hits)
   * @param partNo
   */
  const getProductByPartNo = async(partNo: string): Promise<AlgoliaSearchProduct | null> => {
    return getProducts(partNo);
  };
  /**
   * Gets a single product
   * After getting the result, use Product.create(result.hits)
   */
  const getProductByUrl = async(url: string, indexName = ''): Promise<AlgoliaSearchProduct | null> => {
    return getProducts(null, url, indexName);
  };

  /**
   * Gets multiple products
   * After getting the result from this, you need to use convertRawToProduct to separate the hits
   * Do not change so this is build in here - if this function is inside useAsyncData and returns product classes
   * it fails
   * @param partNos
   */
  const getProductsByPartNo = async(partNos: string[]): Promise<AlgoliaSearchProduct | null> => {
    return getProducts(partNos);
  };

  /**
   * This only gets the exact variant.
   * @import when you create a new Product class from this, it must use limitedVariantLoaded = true
   * @example Product.create([product], true)
   * @param skipVisibilityFilter - bypasses the isPublic/isDisplayOnly/pricelist visibility gate.
   * Use only when the caller already has independent proof the customer legitimately has access
   * to these exact partNos (e.g. they're in the customer's own physical sample inventory per Norce),
   * since storefront visibility rules don't apply there - and the pricelist-based part of that gate
   * can't currently distinguish "not entitled" from "entitled but pricelistIds isn't populated".
   */
  const getExactVariantByPartNo = async(partNos: string[], skipVisibilityFilter = false): Promise<AlgoliaSearchProduct | null> => {
    const filter = {
      distinct: 0,
    } as AlgoliaSearchPayloadRequestOptions;

    // Quote and escape each value, and drop empty ones - an unquoted/empty/special-character
    // partNo anywhere in the list would otherwise make Algolia reject the whole batched filter,
    // silently losing enrichment for every other partNo in the same request.
    const sanitizedPartNos = partNos
      .map((partNo) => partNo?.trim())
      .filter((partNo) => !!partNo)
      .map((partNo) => `"${partNo.replace(/"/g, '\\"')}"`);

    if (sanitizedPartNos.length === 0) { return null; }

    filter.filters = '(partNo:' + sanitizedPartNos.join(' OR partNo:') + ')';
    return getFromAlgolia<ProductRaw[]>(filter, '', '', skipVisibilityFilter);
  };

  /**
   * Gets multiple products
   * After getting the result from this, you need to use convertRawToProduct to separate the hits
   * Do not change so this is build in here - if this function is inside useAsyncData and returns product classes
   * it fails
   * @param urls - array of product urls
   * @param indexName - optional algolia index name
   * @param additionalFilters - optional additional filter string to AND with the url filter
   */
  const getProductsByUrl = async(urls: string[], indexName = '', additionalFilters = ''): Promise<AlgoliaSearchProduct | null> => {
    return getProducts(null, urls, indexName, additionalFilters);
  };

  /**
   * Gets an entire product (all variants) from a partNo or url
   * @param partNo - single or array of part numbers
   * @param url - single or array of urls
   * @param indexName - optional algolia index name
   * @param additionalFilters - optional additional filter string to AND with the main filter
   */
  const getProducts = async(partNo: string | string[] | null = null, url: string | string[] | null = null, indexName = '', additionalFilters = ''): Promise<AlgoliaSearchProduct | null> => {
    const filter = {
      distinct: 0,
    } as AlgoliaSearchPayloadRequestOptions;

    if (partNo) {
      if (Array.isArray(partNo)) {
        filter.filters = '(partNumbers:' + partNo.join(' OR partNumbers:') + ')';
      } else {
        filter.filters = 'partNumbers: ' + partNo;
      }
    } else if (url) {
      if (Array.isArray(url)) {
        filter.filters = '(urls:' + url.join(' OR urls:') + ')';
      } else {
        filter.filters = 'urls:' + url;
      }
    } else {
      console.warn('getProduct called without partNo or url');
      return null;
    }

    if (additionalFilters) {
      filter.filters = `${filter.filters} AND ${additionalFilters}`;
    }

    return getFromAlgolia<ProductRaw[]>(filter, indexName);
  };

  /**
   * If hits contains more than one product, this separates them and makes them into ProductModels
   * @param hits
   * @param partNos - optional, take partNos or urs from getProducts() and insert
   * @param urls
   * @param limitedVariantLoaded - used by search
   */
  const convertRawToProducts = (
    hits: ProductRaw[],
    partNos: string[] = [],
    urls: string[] = [],
    limitedVariantLoaded = false
  ) :AlgoliaSearchProductsItem[] => {
    const productsRawArr = [] as { groupById: number, products: ProductRaw[] }[];

    hits.forEach((hit) => {
      const exists = productsRawArr.findIndex((fi) => fi.groupById === hit.groupById);
      if (exists === -1) {
        productsRawArr.push({
          groupById: hit.groupById,
          products: [ hit ],
        });
      } else {
        productsRawArr[exists].products.push(hit);
      }
    });

    return productsRawArr.map((m) => {
      const output: AlgoliaSearchProductsItem = {
        partNoRequested: '',
        urlRequested: '',
        groupById: m.groupById,
        product: Product.create(m.products, limitedVariantLoaded),
      };
      if (partNos.length) {
        const requested = output.product.partNumbers.find((f) => partNos.includes(f));
        if (requested) {
          output.partNoRequested = requested;
        }
      } else if (urls.length) {
        const requested2 = output.product.urls.find((f) => urls.includes(f));
        if (requested2) {
          output.urlRequested = requested2;
        }
      }

      return output;
    });

  };

  const getBundleProducts = async<T>(partNos: Array<string>|string) : Promise<AlgoliaSearch & {
      hits: T,
  } | null> => {
    const filter = Array.isArray(partNos) ? { filters: 'structureArticlePartNumbers:' + partNos.join(' OR structureArticlePartNumbers:') } : { filters: 'isStructureArticle: true AND structureArticlePartNumbers:' + partNos };
    return getFromAlgolia<T>(filter);
  };

  const getProductByBrandAndCategory = async<T>(brand: string, categories: Array<string>) : Promise<AlgoliaSearch & {
      hits: T,
  } | null> => {
    const filter = { filters: 'brandFilters:' + brand + ' AND categoryFilters:' + categories.join(' OR categoryFilters:') };
    return getFromAlgolia<T>(filter);
  };

  const freeTextSearch = async<T>(query: string, hitsPerPage = 24, indexSuffix = '', nonDistinct = false, includeSamples = false, isPowerOrderSearch = false, optionalFilters?: string[], page?: number, attributesToRetrieve?: string[]) : Promise<AlgoliaSearch & {
      hits: T,
  } | null> => {
    const filter = {
      query,
      hitsPerPage,
    } as AlgoliaSearchPayloadRequestOptions;
    if (nonDistinct) {
      filter.distinct = 0;
    }
    if (page !== undefined) {
      filter.page = page;
    }
    if (optionalFilters?.length) {
      filter.optionalFilters = optionalFilters;
    }
    if (attributesToRetrieve?.length) {
      filter.attributesToRetrieve = attributesToRetrieve;
    }

    const globalContent = useGlobalContentStore();

    /**
     * @important - Kährs exceptions - removes category samples unless includeSamples is true
     * When includeSamples is true, only show SA01, SA02, SA03 samples (or US01, US02 for US market)
     */
    if (!includeSamples) {
      if (!filter.facetFilters) {
        filter.facetFilters = [[
          'categoryFilters:-Samples',
        ]];
      } else {
        filter.facetFilters.push([
          'categoryFilters:-Samples',
        ]);
      }
    } else {
      // Filter to only include SA01, SA02, SA03 samples (or US01, US02 for US market)
      const allowedSampleTypes = globalContent.domainMap?.countryCode === 'us'
        ? ['US01', 'US02']
        : ['SA01', 'SA02', 'SA03'];
      const sampleTypeFilter = allowedSampleTypes
        .map(type => `parametricFilters.ItemSampleType:${type}`)
        .join(' OR ');
      const existingFilters = filter.filters ? `${filter.filters} AND ` : '';
      filter.filters = `${existingFilters}(NOT categoryFilters:Samples OR (${sampleTypeFilter}))`;
    }

    /*  Removes resilient products from search in power order  */
    if (isPowerOrderSearch && globalContent.getHideResilientProductsSearch) {
      if (!filter.facetFilters) {
        filter.facetFilters = [];
      }
      filter.facetFilters.push(['categoryFilters:-Resilient']);
      filter.facetFilters.push(['categoryFilters:-ResilientAccessories']);
    }

    return getFromAlgolia<T>(filter, '', indexSuffix);
  };

  const getPopularProducts = async<T>(hitsPerPage = 24) : Promise<AlgoliaSearch & {
      hits: T,
  } | null> => {
    const filter = {
      hitsPerPage,
      facetFilters: ['categoryFilters:-Samples'],
    };
    return getFromAlgolia<T>(filter);
  };

  const getFromAlgolia = async <T>(
    requestOptions: AlgoliaSearchPayloadRequestOptions,
    indexName = '',
    indexSuffix = '',
    skipVisibilityFilter = false,
    // Also show standard pricelist products to companies with the hideStandard flag (sample requests)
    includeStandardPricelist = false
  ): Promise<(AlgoliaSearch & {
    hits: T
  }) | null> => {

    const globalContent = useGlobalContentStore();
    const userStore = useUserStore();
    const requestOptionsResolved: AlgoliaSearchPayloadRequestOptions = JSON.parse(
      JSON.stringify(requestOptions)
    );
    if (indexName === '') {
      indexName = globalContent.getAlgoliaIndex + indexSuffix;
    }

    try {
      pending.value = true;
      const browserTrackingCookie = useCookie('randomId', { maxAge: 3600 * 24 * 365 });
      if (browserTrackingCookie.value) {
        requestOptionsResolved.userToken = browserTrackingCookie.value;
      }
      requestOptionsResolved.clickAnalytics = true;
      if (!requestOptionsResolved.hitsPerPage) {
        requestOptionsResolved.hitsPerPage = 1000;
      }

      let selection = '(variantStatus:active OR variantStatus:coming OR variantStatus:expiring) '; // NOTE: Keep last whitespace
      if (skipVisibilityFilter) {
        selection += 'AND priceFilters > 0 ';
      } else if (!userStore.loggedIn) {
        if (!globalContent.domainMap?.showNonPublicProducts) {
          selection += 'AND (isPublic:true OR isDisplayOnly:true) ';
        }
      } else {
        if (!globalContent.domainMap?.showNonPublicProducts) {
          const priceLists = (userStore.userProfile.pricelistIds ?? []);
          const allowedPriceLists = priceLists.map((i) => {
            return `availableOnPricelists:${i}`;
          });
          if (allowedPriceLists.length) {
            if (includeStandardPricelist) {
              allowedPriceLists.push('isPublic:true');
            }
            selection += `AND (${allowedPriceLists.join(' OR ')} OR isDisplayOnly:true) `;
          } else {
            selection += 'AND (isPublic:true OR isDisplayOnly:true) ';
          }
        }
      }

      if (requestOptionsResolved.filters && requestOptionsResolved.filters.trim() !== '' && selection.trim() !== '') {
        requestOptionsResolved.filters = selection + ' AND ' + requestOptionsResolved.filters;
      } else if (selection.trim() !== '') {
        requestOptionsResolved.filters = selection;
      }
      const requestOptionsKey = { ...requestOptionsResolved } as Record<string, unknown>;
      delete requestOptionsKey.userToken;
      delete requestOptionsKey.clickAnalytics;
      const cacheKey = `algolia:${indexName}:${stableStringify(requestOptionsKey)}`;
      const cached = responseCache.get(cacheKey);
      if (cached) {
        if (Date.now() - cached.ts < CACHE_TTL_MS) {
          lastResponse.value = cached.data;
          return cached.data;
        }
        responseCache.delete(cacheKey);
      }
      const inflight = pendingCache.get(cacheKey);
      if (inflight) {
        return await inflight;
      }
      const fetchPromise = (async() => {
        let responseData: any = null;

        // useAsyncAlgoliaSearch (which uses useAsyncData) doesn't work when called from async functions on client
        if (import.meta.client) {
          try {
            const { search } = useAlgoliaSearch(indexName);
            responseData = await search({
              query: requestOptionsResolved.query || '',
              requestOptions: requestOptionsResolved,
            });
          } catch (err: any) {
            lastError.value = err;
            return null;
          }
        } else {
          const { data, error } = await useAsyncAlgoliaSearch({
            query: '',
            requestOptions: requestOptionsResolved,
            key: `algolia-${indexName}-${stableStringify(requestOptionsKey)}`,
            indexName,
          });

          if (error?.value?.data) {
            lastError.value = error.value.data;
            lastErrorCode.value = error.value?.statusCode || null;
            return null;
          }

          responseData = data?.value;
        }

        if (responseData) {
          if (requestOptions.page === undefined && responseData.nbHits > requestOptions.hitsPerPage) {
            console.warn('More product variants than can be loaded');
          }

          lastResponse.value = responseData;
          responseCache.set(cacheKey, { ts: Date.now(), data: responseData });
          /**
           * Populate user prices here if b2c
           */
          try {
            if (globalContent.getB2cEnabled && globalContent.getIsB2c) {
              const prices = responseData?.hits?.flatMap((i: any) => {
                const entries = [{
                  partNo: i.partNo,
                  priceInfo: i.priceInfo,
                }];
                // Grouped searches only return one variant per product, also seed cheapest variants price
                if (i.priceFetchPartNo && i.priceFetchPartNo !== i.partNo && i.priceFetchPriceInfo) {
                  entries.push({
                    partNo: i.priceFetchPartNo,
                    priceInfo: i.priceFetchPriceInfo,
                  });
                }
                return entries;
              });
              userStore.addUserPrices(prices, globalContent);
            }
          } catch (error) {
            // Ignore composable context errors during SSR or initial load
            console.warn('Could not access user context for price population:', error);
          }
          return responseData;
        }

        return null;
      })();
      pendingCache.set(cacheKey, fetchPromise);
      try {
        return await fetchPromise;
      } finally {
        pendingCache.delete(cacheKey);
      }
    } catch (e) {
      console.error(e);
      return null;
    } finally {
      pending.value = false;
    }
  };

  return {
    getProductByPartNo,
    getProductsByPartNo,
    getProductByUrl,
    getProductsByUrl,
    convertRawToProducts,
    getBundleProducts,
    getProductByBrandAndCategory,
    pending,
    freeTextSearch,
    getPopularProducts,
    getFromAlgolia,
    getExactVariantByPartNo,
    lastError,
    lastErrorCode,
  };
}
